
import React from "react"
import { formatDate_D_MMM_YYYY, getAddress, getNameProp } from "../../../modules/utilities"
import { COLOR, COLUMN, COMPANY_DASHBOARD_CARD, FORMATTING, POSITION_TYPE, TENSE, TENSE_ENUM } from "../../../modules/constants"
import { getData } from "./NexusCardsCommon"
import getGovObj, { getTotDirectors } from "../../companiesHouse/pageFunctions/complianceFunctions"
import { buildDateValues } from "../../../components/layout/HomeTables/CreateLseCommonTableData"

const { date, float, local, percent, poundsM } = FORMATTING
const borderTop = `2px solid ${COLOR.LIGHT_GREY}`

const companyCardData = props => {
  // console.log('companyCardData props:', props)
  const { entity, hubData } = props
  const { companyProps } = entity
  const { companyProfile, lse: lseProps } = companyProps
  const { activities, previousCompanyNames } = companyProfile
  // console.log('companyProfile:', companyProfile);
  const items = props.items.filter(items => items.level == 1) // We don't want second degree items
  const stdData = [
    {
      id: COMPANY_DASHBOARD_CARD.CORE,
      title: "Core info",
      data: [
        getData({ id: COLUMN.COMPANY_TYPE, value: companyProfile?.type }),
        getData({ id: COLUMN.ADDRESS, value: getAddress(companyProfile?.registeredOfficeAddress ?? entity.address) }),
        getData({ id: COLUMN.MARKET_CAP, type: poundsM, value: lseProps.marketCap }),
        getData({ id: COLUMN.REVENUE, type: poundsM, value: lseProps.revenue }),
        getData({ id: COLUMN.EMPLOYEES, type: local, value: lseProps.employees }),
        getData({ id: COLUMN.ACTIVITIES, ...getActivities(activities) }),
        getData({ id: lseProps.epics.length > 1 ? COLUMN.EPICS : COLUMN.EPIC, value: lseProps.epics.join(', ') }),
        getData({ id: COLUMN.PREVIOUS_NAMES, ...getPreviousNames(previousCompanyNames) }),
        // getData({ label: "Summary", value: lseProps?.description }),
      ]
    }
  ]
  if (lseProps) {
    const { advisers, agmDates, annualReport, governance } = lseProps
    const { age, roleCounts } = governance
    const { dates } = annualReport
    const govObj = getGovObj(lseProps)

    //#region  Advisers table - build the last and next review cells with color coding based on whether the review is overdue, within 12 months, or more than 12 months away.
    const endYearMonth = new Date(dates.accountsDate).getMonth() ?? 0 // Default to January if month is not available
    const lastReview = buildDateValues(endYearMonth, advisers?.lastReviewEndYear)
    const nextReview = buildDateValues(endYearMonth, advisers?.nextReviewEndYear)
    //#endregion

    const lseData = [
      {
        id: COMPANY_DASHBOARD_CARD.BOARD,
        title: "Board",
        data: [
          getData({ id: COLUMN.CEO, ...getOfficerNameAndColor(items, roleCounts, POSITION_TYPE.CEO) }),
          getData({ id: COLUMN.CFO, ...getOfficerNameAndColor(items, roleCounts, POSITION_TYPE.CFO) }),
          getData({ id: COLUMN.EXECS, ...getOfficerNameAndColor(items, roleCounts, POSITION_TYPE.OTHER_EXEC_DIRECTOR) }),
          getData({ id: COLUMN.NON_EXEC_CHAIR, ...getOfficerNameAndColor(items, roleCounts, POSITION_TYPE.CHAIRMAN) }),
          getData({ id: COLUMN.SID, ...getOfficerNameAndColor(items, roleCounts, POSITION_TYPE.SENIOR_NON_EXEC) }),
          getData({ id: COLUMN.INDEP_NON_EXEC, ...getOfficerNameAndColor(items, roleCounts, POSITION_TYPE.NON_EXEC_DIRECTOR) }),
          roleCounts[POSITION_TYPE.OTHER] === 0 ? null : getData({ id: COLUMN.OTHER_DIRS, ...getOfficerNameAndColor(items, roleCounts, POSITION_TYPE.OTHER) }),
          getData({ id: COLUMN.DIRECTORS, borderTop, value: getTotDirectors(roleCounts) }),
          getData({ id: COLUMN.SECRETARY, ...getOfficerNameAndColor(items, roleCounts, POSITION_TYPE.SECRETARY) }),
        ],
      },
      {
        id: COMPANY_DASHBOARD_CARD.GOVERNANCE,
        title: "Governance",
        data: [
          getData({ ...govObj[COLUMN.COMPLIANT] }),
          getData({ type: percent, borderTop, ...govObj[COLUMN.PCNT_INDEP_NON_EXECS] }),
          getData({ ...govObj[COLUMN.INDEP_NON_EXEC_9] }),
          getData({ type: percent, ...govObj[COLUMN.PCNT_FEMALE] }),
          getData({ ...govObj[COLUMN.SENIOR_FEMALE] }),
          getData({ ...getEmDirectors(govObj) }),
          getData({ borderTop, id: COLUMN.NON_BRIT, value: governance.nonBrit }),
          getData({ id: COLUMN.AGE_AVG, type: float, value: age.avg }),
          getData({ id: COLUMN.AGE_SD, type: float, value: age.sd }),
        ]
      },
      {
        id: COMPANY_DASHBOARD_CARD.DATES,
        title: "Dates",
        data: [
          getData({ id: COLUMN.CREATED, ...getFoundedDate(hubData.fromDate) }),
          getData({ id: COLUMN.END_OF_FY_OF_LAST_PUBLISHED_AR, borderTop, type: date, value: dates.accountsDate }),
          getData({ id: COLUMN.PUB_DATE_OF_LAST_PUBLISHED_AR, type: date, value: dates.publicationDate }),
          getData({ id: COLUMN.DAYS_BEFORE_PUBLICATION, value: dates.diffDays }),
          getData({ id: COLUMN.DUE_DATE, type: date, value: dates.dueDate }),
          getData({ id: COLUMN.LAST_AGM, borderTop, type: date, value: agmDates.last }),
        ],
      },
      {
        id: COMPANY_DASHBOARD_CARD.ADVISERS,
        title: "Advisers",
        data: [
          getData({ id: COLUMN.AUDITOR, value: advisers?.auditor }),
          getData({ id: COLUMN.RECRUITING_ADVISER, value: advisers?.recruitingAdviser }),
          getData({ id: COLUMN.REMUNERATION_ADVISER, value: advisers?.remunerationAdviser }),
          getData({ id: COLUMN.EXTERNAL_REVIEW, borderTop, value: advisers?.lastReviewFacilitator, link: advisers?.urlToMentionOfReviewInAr, newTab: true }),
          getData({ id: COLUMN.LAST, value: lastReview.display }),
          getData({
            id: COLUMN.NEXT,
            color: nextReview.status === 1 ? COLOR.GOOD : nextReview.status === 2 ? COLOR.WARN : null,
            value: nextReview.display
          }),
        ],
      },
    ]
    return stdData.concat(lseData)
  }
  else return stdData
}

const getEmDirectors = govObj => {
  // NB tooltipTitle set to null so that we use 'LEGEND' tooltipTitle
  return {
    ...govObj[COLUMN.EM_DIRs],
    tooltipTitle: null,
    valueTooltip: govObj[COLUMN.EM_DIRs].tooltip,
  }
}

const getFoundedDate = fromDate => fromDate === "Jan 1970"
  ? {
    value: "unknown"
  }
  : {
    type: date,
    value: fromDate,
  }

const getOfficerNameAndColor = (items, position_enum, roleCounts) => {
  // Returns object { value (color) }
  // If value (officer count) is 1, value is set to the name of the current
  // officer with position matching position_enum. Else value is unchanged.

  var items = items.filter((i, index) => (
    index > 0 && // The company could have a matching postion, left over from person view!
    i.entity?.position?.type === position_enum &&
    (i.entity?.position?.tense === TENSE_ENUM.CURRENT || i.entity?.position?.tense === TENSE_ENUM.NOTICE)))
  switch (items.length) {
    case 0:
      return { value: position_enum[roleCounts] }
    case 1:
      return getOfficerNameAndColorObj(items[0])
    default:
      return { value: items.map(item => getOfficerNameAndColorObj(item)) }
  }
}

const getOfficerNameAndColorObj = item => {
  return {
    color: TENSE[item.entity.position.tense].COLOR,
    valueTooltip: valueTooltip(item),
    value: getNameProp.toPresent(item.entity?.name),
  }
}

const valueTooltip = item => {
  // console.log('item:',item)
  const value = getNameProp.toPresent(item.entity?.name)
  const role = item.role
  const from = formatDate_D_MMM_YYYY(item.connectionStart)
  const to = formatDate_D_MMM_YYYY(item.connectionEnd)

  return item.connectionEnd
    ? (<div >{value}<br />{role}<br />Appointed to Board {from}<br />Resigning from Board {to}</div>)
    : (<div> {value} < br /> {role} < br /> Appointed to Board {from}</div >)
}

const getPreviousNameObj = previousName => {
  const value = previousName.name
  const from = formatDate_D_MMM_YYYY(previousName.effective_from)
  const ceased_on = formatDate_D_MMM_YYYY(previousName.ceased_on)
  const valueTooltip = (<div>{value}<br />{from} - {ceased_on}</div>)
  return { valueTooltip, value }
}

const getPreviousNames = previousNames => {
  // If one previous name, return object with value and dates tooltip. Else returns an array of objects
  if (previousNames.length === 1)
    return getPreviousNameObj(previousNames[0])
  else
    return { value: previousNames.map(previousName => getPreviousNameObj(previousName)) }
}

const getActivities = activities => {
  // Returns object with value. If one activity value is string, else value is array
  if (activities.length === 1)
    return { value: activities[0] }
  else
    return { value: activities.map(activity => ({ value: activity })) }
}

export default companyCardData
